import { DashboardAccountInfo } from "@ecountabl/lib"
import React, { useContext, useMemo } from "react"
import { StyleSheet, Text, View } from "react-native"
import { ShadowView, textStyles } from "./style"
import { ThemeContext } from "./utility/context/ThemeContext"
import { MAX_CONTENT_WIDTH } from "./config"
import { AccountCard } from "./account-card"
import Bullet from "./style/bullet"
import { useAppSelector } from "./store"
import DragScrollContainer from "./utility/drag-scroll-container"
import Haptics from "./utility/haptics"
import { CommonActions, Link } from "@react-navigation/native"
import Badge from "./utility/badge"
import { AccountsSkeleton } from "./accounts-skeleton"

const Accounts = (): React.ReactElement => {
  const {
    accounts,
    status: accountsStatus,
    getAccountsError,
    institutions
  } = useAppSelector(state => state.accountsSlice)

  const theme = useContext(ThemeContext).theme
  const { primary, danger, bgMain, textMain, white } = theme.colors

  const showSkeleton = accountsStatus === 'idle' || accountsStatus === 'loading'

  const institutionsNeedingRelink = useMemo(() => {
    return institutions.filter((institution) => institution.error)
  }, [institutions])


  const renderAccountCard = (account: DashboardAccountInfo) => {
    if (account.error) {
      return (
        <AccountCard
          account={account}
        />
      )
    }

    return (
      <AccountCard
        account={account}
      />
    )
  }

  const relinkMessage = useMemo(() => {
    switch (institutionsNeedingRelink.length) {
      case 0: return ""
      case 1: return `There was an issue retrieving account information from ${institutionsNeedingRelink[0].name}. To reconnect it, please go to Manage Accounts`
      default: return `There was an issue retrieving account information from ${institutionsNeedingRelink.length} institutions. To reconnect your accounts, please go to Manage Accounts`
    }
  }, [institutionsNeedingRelink])

  return (
    <View style={{alignSelf: "center", width: "100%", maxWidth: MAX_CONTENT_WIDTH}}>
      {
        showSkeleton ? (
          <AccountsSkeleton itemCount={3} cardWidth={350} />
        ) : (
          <DragScrollContainer
            id="accounts"
            data={accounts || []}
            renderItem={({item}) => renderAccountCard(item)}
            keyExtractor={(item, index) => `${index}`}
            contentContainerStyle={{paddingHorizontal: 16}}
          />
        )
      }
      {
        institutionsNeedingRelink.length ? (
          <ShadowView
            style={[
              styles.summaryCard,
              {
                backgroundColor: bgMain,
                marginHorizontal: 16,
                marginTop: accounts.length > 0 ? 10 : 0,
              }
            ]}
          >
            <Text style={[textStyles.body, {color: textMain}]}>
              {relinkMessage}
            </Text>
          </ShadowView>
        ) : null
      }
      {// error message for accounts
        getAccountsError ? (
          <ShadowView
            style={[
              styles.summaryCard,
              {
                backgroundColor: bgMain,
                marginHorizontal: 16,
                marginTop: accounts.length > 0 ? 10 : 0,
                flexDirection: "row",
                justifyContent: "flex-start",
              }
            ]}
          >
            <Bullet color={danger} size={6} style={{paddingRight: 6}} />
            <Text style={[textStyles.body,{color: textMain}]}>
              {getAccountsError}
            </Text>
          </ShadowView>
        ) : null
      }
      {
        // if accounts exist, add to content with button to link accounts
        accounts.length || institutions.length ?
        <View style={{paddingVertical: 8, marginLeft: 16 * 2, alignSelf: "flex-start"}}>
          <Link
            onPress={() => Haptics.selection()}
            action={
              CommonActions.navigate({
                name: "Settings",
                params: {
                  screen: "ManageAccounts",
                  pop: true
                }
              })
            }
            href={"/settings/manage-accounts"}
          >
            {
              institutionsNeedingRelink.length ? (
                <Badge
                  icon={<Text style={{color: white}}>{institutionsNeedingRelink.length}</Text>}
                  style={{backgroundColor: danger, marginRight: 5}}
                />
              ): null
            }
            <Text
              style={[
                textStyles.h4,
                {
                  color: primary,
                }
              ]}
            >
              Manage Accounts
            </Text>
          </Link>
        </View>
          : null
      }
    </View>
  )
}

export default Accounts

const styles = StyleSheet.create({
  impactBankingProductSection: {
    marginHorizontal: 16,
    marginTop: 16,
    borderRadius: 10,
    flexDirection: "row",
    flexWrap: "wrap",
    overflow: "hidden",
    alignItems: "center"
  },
  infoContainer: {
    padding: 16,
    flex: 1
  },
  summaryCard: {
    flexDirection: "column",
    justifyContent: "space-between",
    gap: 5,
    borderRadius: 10,
    padding: 15,
  },
})